import * as React from 'react';
import * as _ from 'underscore';

import { IGlob } from '../../ts/Glob';
import DirsTemplatesFlat from '../DirsTemplatesFlat';
import { ngettext } from '../../../../_common/platform/ts/ngettext';
import { IMarkedItems } from '../../ts/Interfaces';
import { ISelectedItem } from '../DirsTreeFlat';

export interface ITemplatesExportValue {
    dirs: string[];
    templates: string[];
}

interface IStateValue {
    markedDirs?: IMarkedItems;
    markedTemplates?: IMarkedItems;
}

function createStateValue(value: ITemplatesExportValue, selectedItems: ISelectedItem[]) {
    let result: IStateValue;

    if (value != null) {
        const markedDirs: IMarkedItems = {};
        const markedWorkspaces: IMarkedItems = {};

        if (selectedItems != null) {
            selectedItems.forEach((item) => {
                if (item.type === 0) {
                    markedDirs[item.id] = item.title;
                } else {
                    markedWorkspaces[item.id] = item.title;
                }
            });
        }

        result = {
            markedDirs: markedDirs,
            markedTemplates: markedWorkspaces
        };
    } else {
        result = {
            markedDirs: {},
            markedTemplates: {}
        };
    }

    return result;
}

function getResultValue(value: IStateValue) {
    const newValue: ITemplatesExportValue = {
        dirs: _.keys(value.markedDirs),
        templates: _.keys(value.markedTemplates)
    };

    const selectedItems: ISelectedItem[] = [];

    _.each(value.markedDirs, (title, id) => {
        selectedItems.push({ type: 0, id: id, title: title });
    });
    _.each(value.markedTemplates, (title, id) => {
        selectedItems.push({ type: 1, id: id, title: title });
    });

    return { newValue, selectedItems };
}

function getSelectedMessage(value: IStateValue) {
    const markedDirs = Object.values(value.markedDirs);
    const dirsNumber = markedDirs.length;
    const markedTemplates = Object.values(value.markedTemplates);
    const templatesNumber = markedTemplates.length;

    if (dirsNumber === 0 && templatesNumber === 0) {
        return [<span key={'ask-ws-span'}>{'Select folders and widgets to export.'}</span>];
    }

    const result = _.compact([
        dirsNumber > 0 ? (
            <div key={'dirs-div-ws'} className="selected-message-block">
                <strong>{markedDirs.join(', ')}</strong>
                <span>{ngettext('folder', 'folders', dirsNumber)}</span>
            </div>
        ) : null,

        templatesNumber > 0 ? (
            <div key={'item-div-ws'} className="selected-message-block">
                <strong>{markedTemplates.join(', ')}</strong>
                <span>{ngettext('widget', 'widgets', templatesNumber)}</span>
            </div>
        ) : null
    ]);

    if (result.length === 2) {
        result.splice(1, 0, <span key={'and-span-ws'}>{'and'}</span>);
    }

    result.unshift(<span key={'you-span-ws'}>{'You have selected'}</span>);
    return result;
}

export default function TemplatesExport(props: {
    value: ITemplatesExportValue;
    selectedItems: ISelectedItem[];
    glob: IGlob;
    onChange(value: ITemplatesExportValue, selectedItems: any[]): void;
}) {
    const [value, setValue] = React.useState<IStateValue>(createStateValue(props.value, props.selectedItems));

    const processOnChange = React.useCallback(
        (value: IStateValue) => {
            if (props.onChange != null) {
                const { newValue, selectedItems } = getResultValue(value);

                props.onChange(newValue, selectedItems);
            }

            setValue(value);
        },
        [props.onChange]
    );

    const handleOnDirsMark = (marked: IMarkedItems) => {
        processOnChange({
            ...value,
            markedDirs: marked
        });
    };

    const handleOnTemplatesMark = (marked: IMarkedItems) => {
        processOnChange({
            ...value,
            markedTemplates: marked
        });
    };

    const handleOnMark = (dirs: IMarkedItems, templates: IMarkedItems) => {
        processOnChange({
            ...value,
            markedDirs: dirs,
            markedTemplates: templates
        });
    };

    return (
        <div className="templates-export">
            <div className="selected-message">{getSelectedMessage(value)}</div>

            <DirsTemplatesFlat
                instanceID={'templates-export'}
                markMode={true}
                markedDirs={value.markedDirs}
                markedTemplates={value.markedTemplates}
                glob={props.glob}
                onDirsMark={handleOnDirsMark}
                onTemplatesMark={handleOnTemplatesMark}
                onMark={handleOnMark}
            />
        </div>
    );
}
